import {WidgetConfig} from "types/config";
import {ChatWidget} from "classes/chat/chatWidget";
import Alpine, {Alpine as AlpineNamespace} from "@alpinejs/csp";

import {ChatType, ClosePageContentType, ErrorTypes, MessageSendState, Salutation, SystemMessage} from "types/enums";
import BotChat from "classes/botChat";
import {MessageFactory} from "classes/factory/messageFactory";
import AgentChat from "classes/agentChat";
import {AgentMessageObject, BotMessageObject, OutputMessageObject} from "types/chat";
import ComponentConfig from "config/componentConfig";
import {LightMarkdownConverter} from "classes/utils/LightMarkdownConverter";
import feedbackButtons from '../../../../html/widget_feedback_buttons.html?raw';
import {IHistoryMessageState} from "./feedbackComponent";
import {IFeedbackState} from "classes/chat/messageHistoryMessage";
import DOMPurify from "dompurify";
import {
    CHAT_MESSAGE_INPUT_DISABLE_EVENT,
    CHAT_MESSAGE_INPUT_ENABLE_EVENT,
    CHAT_MESSAGE_INPUT_FILL_RECORDED_TEXT_EVENT,
    CHAT_MESSAGE_INPUT_HAS_TEXT_EVENT,
    CHAT_MESSAGE_INPUT_REQUEST_HAS_TEXT_EVENT,
    IChatMessageInputHasTextEventDetails,
    IChatMessageInputFillRecordedTextEventDetails
} from "types/events";
import XDataContext = AlpineNamespace.XDataContext;
import AlpineComponent = AlpineNamespace.AlpineComponent;
import AlpineMagics = AlpineNamespace.Magics;

type MessageContentFactory = (
    widget: ChatWidget,
    widgetConfig: WidgetConfig,
    componentConfig: ComponentConfig) => AlpineComponent<IMessageContent>;

export interface IMessageContent {
    placeholder: string;
    readonly salutation: string;
    readonly maxChatTextLength: number;
    messages: OutputMessageObject[];
    botHistoryMessages: OutputMessageObject[];
    spinnerVisibility: boolean;
    agentTypingVisibility: boolean;
    switchToAgentChatButtonVisibility: boolean;
    showBotSuggestions: boolean;
    showBotGreeting: boolean;
    initClientTyping: boolean;
    agentConnectMessageAdded: boolean;
    onlineAgentName: string;
    isError: boolean;
    systemErrorMessage: string | null;
    showSystemErrorMessage: boolean;
    systemMessage: string;
    suggestionText: string;
    currentMessage: string;
    chatMessageCharCount: number;

    setSystemErrorMode( errorType: string ): void;
    resetSystemErrorMode( errorType: string ): void;
    retryConnect( ): void;
    showReconnectButton(): boolean;
    changeMessageStatus( id: string, status: MessageSendState ): void;
    addSystemMessage(id: string): void;
    removeSystemMessage(id: string, onError?: boolean): void;
    removeAllSystemMessages(): void;
    updateCharCount(): void;
    typeMessage():void;
    sendMessage():void;
    sendSuggestion(): void;
    getMessageText(message: OutputMessageObject):string;
    getBotHistoryMessageText(message: OutputMessageObject):string;
    isSalutationSie(): boolean;
    getSendingStatus(message: OutputMessageObject):string;
    getSendingStatusTooltip(message: OutputMessageObject):string;
    switchToAgentChat():void;
    disableMessageInput(): void;
    enableMessageInput(): void;
    updateOnlineAgentName(name: string):void;
    closeChat(isError: boolean|null): void;
    closeConnection(): void;
}

class MessageContent implements IMessageContent, XDataContext {
    private static readonly SPEECH_TO_TEXT_ATTRIBUTE = 'data-speech-to-text';
    placeholder: string;
    readonly salutation: string;
    readonly maxChatTextLength: number;
    messages: OutputMessageObject[] = [];
    botHistoryMessages: OutputMessageObject[] = [];
    spinnerVisibility: boolean = false;
    agentTypingVisibility: boolean = false;
    switchToAgentChatButtonVisibility: boolean = true;
    showBotSuggestions: boolean = false;
    showBotGreeting: boolean = false;
    initClientTyping: boolean = true;
    agentConnectMessageAdded: boolean = false;
    onlineAgentName: string = '';
    isError: boolean = false;
    systemErrorMessage: string | null = null;
    showSystemErrorMessage: boolean = false;
    systemMessage: string =  "";
    suggestionText: string = '';
    currentMessage: string = '';
    chatMessageCharCount: number = 0;

    private hasSentFirstMessage: boolean = false;
    private readonly converter: LightMarkdownConverter = new LightMarkdownConverter();
    private readonly botGreeting: string = '';

    private timerClientTyping: number | undefined = undefined;
    private timerAgentTyping: number | undefined = undefined;
    private workingSystemMessages:string[] = [];
    private chatSuggestions: string[] = [];

    private currentMessageIndex: number = 0;
    private currentBotHistoryMessageIndex: number = 0;
    private recordingStatusObserver: MutationObserver | null = null;

    private readonly handleChatMessageInput = (event: Event): void => {
        const target = event.target;
        if (!(target instanceof HTMLTextAreaElement)) {
            return;
        }

        if (event instanceof InputEvent && event.isTrusted) {
            this.setInputSpeechToTextAttribute(target, false);
        }

        this.dispatchChatMessageInputHasTextEvent(target.value);
        this.updateCurrentMessageHasInputClass();
    };

    private readonly handleChatMessageInputFocusChange = (): void => {
        this.updateCurrentMessageHasInputClass();
    };

    private readonly handleChatMessageInputFillRecordedText = (event: Event): void => {
        const customEvent = event as CustomEvent<IChatMessageInputFillRecordedTextEventDetails>;
        const text = customEvent.detail?.text ?? '';
        const input = this.messageContentComponent.$refs.chatMessageInput;

        if (!(input instanceof HTMLTextAreaElement)) {
            return;
        }

        const maxLength = input.maxLength;
        const normalizedText = maxLength > 0 ? text.slice(0, maxLength) : text;

        input.value = normalizedText;
        this.setInputSpeechToTextAttribute(input, normalizedText.trim() !== '');
        input.dispatchEvent(new Event('input', { bubbles: true }));
    };

    private readonly handleChatMessageInputDisable = (): void => {
        this.disableMessageInput();
    };

    private readonly handleChatMessageInputEnable = (): void => {
        this.enableMessageInput();
    };

    private readonly handleChatMessageInputRequestHasText = (): void => {
        const input = this.messageContentComponent.$refs.chatMessageInput;

        if (!(input instanceof HTMLInputElement)) {
            return;
        }

        this.dispatchChatMessageInputHasTextEvent(input.value);
    };

    private dispatchChatMessageInputHasTextEvent(value: string): void {
        const hasText = value.trim() !== '';
        const detail: IChatMessageInputHasTextEventDetails = {
            hasText,
            value,
        };

        window.dispatchEvent(new CustomEvent(CHAT_MESSAGE_INPUT_HAS_TEXT_EVENT, {
            detail,
        }));
    }

    private updateCurrentMessageHasInputClass(): void {
        const input = this.messageContentComponent.$refs.chatMessageInput;
        if (!(input instanceof HTMLTextAreaElement)) {
            return;
        }

        const currentMessage = input.closest('#chat-widget-current-message');
        if (!(currentMessage instanceof HTMLDivElement)) {
            return;
        }

        const recordingStatus = currentMessage.querySelector('.recording-status-text');
        const hasRecordingStatusText = recordingStatus instanceof HTMLElement
            && (recordingStatus.textContent?.trim() ?? '') !== '';
        const isRecordingActive = recordingStatus instanceof HTMLElement
            && recordingStatus.querySelector('.recording-visualizer') instanceof HTMLElement;
        const hasInputValue = input.value.trim() !== '';
        const hasFocus = input.matches(':focus');
        const hasInputClass = !isRecordingActive && (hasFocus || hasInputValue || hasRecordingStatusText);

        input.classList.toggle('recording-active', isRecordingActive);
        currentMessage.classList.toggle('has-input', hasInputClass);
    }

    private observeRecordingStatusText(): void {
        if (this.recordingStatusObserver !== null) {
            this.recordingStatusObserver.disconnect();
            this.recordingStatusObserver = null;
        }

        const input = this.messageContentComponent.$refs.chatMessageInput;
        if (!(input instanceof HTMLTextAreaElement)) {
            return;
        }

        const currentMessage = input.closest('#chat-widget-current-message');
        if (!(currentMessage instanceof HTMLDivElement)) {
            return;
        }

        const recordingStatus = currentMessage.querySelector('.recording-status-text');
        if (!(recordingStatus instanceof HTMLElement)) {
            return;
        }

        this.recordingStatusObserver = new MutationObserver((): void => {
            this.updateCurrentMessageHasInputClass();
        });

        this.recordingStatusObserver.observe(recordingStatus, {
            childList: true,
            characterData: true,
            subtree: true,
        });
    }

    private isInputSpeechToText(input: HTMLTextAreaElement): boolean {
        return input.getAttribute(MessageContent.SPEECH_TO_TEXT_ATTRIBUTE) === 'true';
    }

    private setInputSpeechToTextAttribute(input: HTMLTextAreaElement, isSpeechToText: boolean): void {
        input.setAttribute(
            MessageContent.SPEECH_TO_TEXT_ATTRIBUTE,
            isSpeechToText ? 'true' : 'false'
        );
    }

    constructor(
        private readonly widget: ChatWidget, 
        private readonly widgetConfig: WidgetConfig,
        private readonly componentConfig: ComponentConfig,)
    {
        this.placeholder =  this.widgetConfig.getQuestionPlaceholder();
        this.salutation = this.widgetConfig.salutation === Salutation.SIE ? 'Ihnen' : 'Dir';
        this.maxChatTextLength = this.widgetConfig.getMaxChatTextLength();
        this.converter = new LightMarkdownConverter();
        this.botGreeting = this.widgetConfig.getBotGreeting();
    }
    init() {
        if(this.componentConfig.forceAgentChat) {
            this.addAgentConnectSystemMessage();
        }

        const chatMessageInput = this.messageContentComponent.$refs.chatMessageInput;
        if (chatMessageInput instanceof HTMLTextAreaElement) {
            chatMessageInput.removeEventListener('input', this.handleChatMessageInput);
            chatMessageInput.addEventListener('input', this.handleChatMessageInput);
            chatMessageInput.removeEventListener('focus', this.handleChatMessageInputFocusChange);
            chatMessageInput.addEventListener('focus', this.handleChatMessageInputFocusChange);
            chatMessageInput.removeEventListener('blur', this.handleChatMessageInputFocusChange);
            chatMessageInput.addEventListener('blur', this.handleChatMessageInputFocusChange);
            this.dispatchChatMessageInputHasTextEvent(chatMessageInput.value);
        }

        this.observeRecordingStatusText();
        this.updateCurrentMessageHasInputClass();
        this.updateCharCount();

        window.removeEventListener(
            CHAT_MESSAGE_INPUT_FILL_RECORDED_TEXT_EVENT,
            this.handleChatMessageInputFillRecordedText as EventListener
        );
        window.addEventListener(
            CHAT_MESSAGE_INPUT_FILL_RECORDED_TEXT_EVENT,
            this.handleChatMessageInputFillRecordedText as EventListener
        );

        window.removeEventListener(
            CHAT_MESSAGE_INPUT_DISABLE_EVENT,
            this.handleChatMessageInputDisable as EventListener
        );
        window.addEventListener(
            CHAT_MESSAGE_INPUT_DISABLE_EVENT,
            this.handleChatMessageInputDisable as EventListener
        );

        window.removeEventListener(
            CHAT_MESSAGE_INPUT_ENABLE_EVENT,
            this.handleChatMessageInputEnable as EventListener
        );
        window.addEventListener(
            CHAT_MESSAGE_INPUT_ENABLE_EVENT,
            this.handleChatMessageInputEnable as EventListener
        );

        window.removeEventListener(
            CHAT_MESSAGE_INPUT_REQUEST_HAS_TEXT_EVENT,
            this.handleChatMessageInputRequestHasText as EventListener
        );
        window.addEventListener(
            CHAT_MESSAGE_INPUT_REQUEST_HAS_TEXT_EVENT,
            this.handleChatMessageInputRequestHasText as EventListener
        );

        // Zur letzten eingegebenen Nachricht scrollen
        this.messageContentComponent.$watch( 'messages', async ()=> {
            await this.messageContentComponent.$nextTick();

            const chatWidgetMessagesContent = this.messageContentComponent.$refs.scrollContainer;
            if (chatWidgetMessagesContent !== null) {
                let allMessages = chatWidgetMessagesContent.querySelectorAll('[class="chat-widget-message"]'); // Select only elements, that don't have additional classes
                if (this.agentTypingVisibility) {
                    chatWidgetMessagesContent.scrollTo({
                        top: chatWidgetMessagesContent.scrollHeight,
                        behavior: 'smooth'
                    });
                }
                else {
                    allMessages[allMessages.length - 1]?.scrollIntoView({
                        behavior: 'smooth',
                        block: 'start'
                    });
                }
            }
        });

        this.messageContentComponent.$root.addEventListener('add-message', (event:any) => {
            let { text, id, type, isAgentChat, author, time, messageId, responseId, isError, isHistoryMessage } = event.detail;

            this.isError = isError;
            if(isError) {
                this.removeSystemMessage(SystemMessage.LOAD_CHAT, true);
            }

            if(isAgentChat) {
                this.agentTypingVisibility = false;
                clearTimeout(this.timerAgentTyping);

                if(this.agentConnectMessageAdded || (isHistoryMessage && (author === 'Support-Bot' || author === 'Live Support' || author === 'Live-Support'))) {
                    this.agentConnectMessageAdded = false;
                    this.removeSystemMessage(SystemMessage.CONNECT_TO_AGENT);

                    if(!isError) {
                        this.addMessageToContent('<span class="agent" x-text="onlineAgentName"></span> ist online <span class="online-symbol"></span>',
                            'agent-online', ChatType.AGENT_CHAT , 'system-online', '', '', '', '', true);

                        this.enableMessageInput();
                    }
                }

                if(!isError && type === 'answer') {
                    this.updateOnlineAgentName(author);
                }
                text = this.converter.toHtml(text);
            }

            let chatType = isAgentChat ? ChatType.AGENT_CHAT : ChatType.BOT_CHAT;

            text = this.sanitizeHtml(text);
            this.addMessageToContent(text, id, chatType, type, author, time, messageId, responseId);

            if(this.widget.activeChat instanceof BotChat) {
                this.enableMessageInput();
            }
        });

        this.messageContentComponent.$root.addEventListener('add-bot-history-message', (event:any) => {
            let { text, id, type, author, time, messageId, responseId, isLastMessage } = event.detail;
            const feedbackState: IFeedbackState = event.detail.feedbackState;

            text = this.sanitizeHtml(text);

            const isAgentChat = this.widget.activeChat?.isAgentChat() ?? false;

            const isFeedbackInteractive: boolean = (isLastMessage && type === 'answer' && !isAgentChat);
            let messageHistoryState: IHistoryMessageState = {
                isInteractive: isFeedbackInteractive,
                initialRating: null,
            };

            if (null !== feedbackState) {
                messageHistoryState = {
                    ...messageHistoryState,
                    initialRating: feedbackState.rating ?? null,
                    initialReasonGiven: feedbackState.reasonGiven ?? false,
                };
            }

            let outputMessage = MessageFactory.createOutputMessage(
                text,
                id,
                ChatType.BOT_CHAT,
                type,
                author,
                time,
                messageId,
                responseId,
                messageHistoryState
            );

            this.botHistoryMessages.push(outputMessage);
            this.initializeFeedbackComponent(ChatType.BOT_CHAT, outputMessage);

            // "Persönlicher Chat" in SW starten
            if(isAgentChat &&
                this.componentConfig.forceAgentChat &&
                this.componentConfig.queue !== "bestell")
            {
                this.widget.addAiHistoryMessage(outputMessage, this.widgetConfig.getMaxAiHistoryTextLength());

                if(isLastMessage) {
                    this.widget.setLastAiMessageFlag()
                    this.widget.initAiHistoryMessageSend();
                }
            }

            // if(this.widget.activeChat instanceof BotChat) {
            //     this.enableMessageInput();
            // }
        });

        this.messageContentComponent.$root.addEventListener('set-agent-typing', (event:any) => {
            let { duration } = event.detail;

            this.agentTypingVisibility = true;
            clearTimeout(this.timerAgentTyping);

            this.timerAgentTyping = setTimeout(() => {
                this.agentTypingVisibility = false;
            }, duration);
        });

        this.messageContentComponent.$root.addEventListener('send-current-message', () => {
            this.sendMessage();
        });

        this.messageContentComponent.$root.addEventListener('show-bot-suggestions', () => {
            if(this.componentConfig.queue !== 'bestell') {
                this.showBotSuggestions = true;
                this.chatSuggestions = Array.from(this.widgetConfig.getSuggestions());
            }
        });

        this.messageContentComponent.$root.addEventListener('show-bot-greeting', () => {
            this.showBotGreeting = true;
        });

        this.messageContentComponent.$root.addEventListener('close-chat-by-agent', (event:any) => {
            let { leftByBot } = event.detail;
            let closeContentType = ClosePageContentType.PRINT;
            if (leftByBot) {
                closeContentType = ClosePageContentType.NO_AGENT_AVAILABLE;
            }
            this.widget.initCloseContent(closeContentType);
            if(this.componentConfig.forceAgentChat) {
                this.widget.getChatHelper().initAgentAvailabilityCheck();
            }
        });

        this.messageContentComponent.$root.addEventListener('add-system-loader', (event:any) => {
            let { messageId } = event.detail;
            this.addSystemMessage(messageId);
        });
        this.messageContentComponent.$root.addEventListener('remove-system-loader', (event:any) => {
            let { messageId } = event.detail;
            this.removeSystemMessage(messageId);
        });
        this.messageContentComponent.$root.addEventListener('remove-all-system-loader', () => {
            this.removeAllSystemMessages();
        });
        this.messageContentComponent.$root.addEventListener('trigger-system-error', (event:any) => {
            let { errorType } = event.detail;
            this.setSystemErrorMode(errorType);
        });
        this.messageContentComponent.$root.addEventListener('reset-system-error', (event:any) => {
            let { errorType } = event.detail;
            this.resetSystemErrorMode(errorType);
        });
        this.messageContentComponent.$root.addEventListener('change-message-status', (event:any) => {
            let { id, status } = event.detail;
            this.changeMessageStatus(id, status);
        });
        this.messageContentComponent.$root.addEventListener('close-chat', (event:any) => {
            let { isError } = event.detail;
            this.closeChat(isError);
        });

        this.messageContentComponent.$root.addEventListener("click", ( event:any ) => {
            if(!(event.target instanceof HTMLAnchorElement) || !event.target.className.includes('reportingLink')) {
                return;
            }
            const messageId = event.target.dataset.messageId;
            const responseId = event.target.dataset.responseId;
            if (messageId === undefined || responseId === undefined) {
                return;
            }
            event.preventDefault();
            event.stopPropagation();
            this.widget.triggerReportingEvent(messageId, responseId, event.target.href);
        })
    }

    private get messageContentComponent(): AlpineMagics<this> {
        return this as unknown as AlpineMagics<this>;
    }

    setSystemErrorMode( errorType: string ): void {
        this.systemErrorMessage = this.widgetConfig.getErrorMessage(errorType);
        this.showSystemErrorMessage = true;

        this.removeAllSystemMessages();
        this.disableMessageInput();
        this.switchToAgentChatButtonVisibility = false;
    }

    resetSystemErrorMode( errorType: string ): void {
        if (this.systemErrorMessage !== this.widgetConfig.getErrorMessage(errorType)) {
            return;
        }

        this.enableMessageInput();
        if (this.componentConfig.allowAgentChat) {
            this.switchToAgentChatButtonVisibility = true;
        }
        this.systemErrorMessage = null;
        this.showSystemErrorMessage = false;
    }

    retryConnect( ): void {
        if(this.widget.activeChat instanceof AgentChat) {
            this.resetSystemErrorMode(ErrorTypes.INIT_CONNECTION_ERROR);
            this.addSystemMessage( SystemMessage.LOAD_CHAT );

            this.widget.retryConnect()
                .then(() => {
                    this.removeSystemMessage( SystemMessage.LOAD_CHAT );
                } );
        }
    }

    showReconnectButton(): boolean {
        return this.systemErrorMessage === this.widgetConfig.getErrorMessage(ErrorTypes.INIT_CONNECTION_ERROR);
    }

    changeMessageStatus( id: string, status: MessageSendState ): void {
        if(id === "ai-history" && status === MessageSendState.RECEIVED) {
            this.widget.initAiHistoryMessageSend();
        }

        // Findet die Nachricht mit der passenden id und setzt den Status
        const msg = this.messages.find((m: any) => m.id === id);
        if (msg) {
            msg.status = status;
        }
    }

    addSystemMessage(id: string): void {
        this.systemMessage = this.widgetConfig.getSystemMessage(id);
        if ( id === "load_bot_answer") {
            this.agentTypingVisibility = true;
        }
        else {
            this.spinnerVisibility = true;
        }

        this.workingSystemMessages.push(id);
        this.disableMessageInput();
    }

    removeSystemMessage(id: string, onError: boolean = false): void {
        const index = this.workingSystemMessages.indexOf(id);
        if (index !== -1) {
            this.workingSystemMessages.splice(index, 1);
        }

        if (this.workingSystemMessages.length > 0) {
            return;
        }
        this.agentTypingVisibility = false;
        this.spinnerVisibility = false;
        this.enableMessageInput();
    }

    removeAllSystemMessages(): void {
        this.workingSystemMessages = [];
        this.agentTypingVisibility = false;
        this.spinnerVisibility = false;
        this.enableMessageInput();
    }

    typeMessage():void{
        if(!(this.widget.activeChat instanceof AgentChat)) {
            return;
        }

        this.widget.setIsClientTyping(true);
        if(this.initClientTyping) {
            this.widget.initClientTyping();
            this.initClientTyping = false;
        }

        clearTimeout(this.timerClientTyping);

        this.timerClientTyping = setTimeout(() => {
            this.widget.setIsClientTyping(false);
        }, 5000);
    }

    updateCharCount(): void {
        const input = this.messageContentComponent.$refs.chatMessageInput;
        if (!(input instanceof HTMLTextAreaElement)) {
            this.chatMessageCharCount = this.currentMessage.length;
            return;
        }

        this.currentMessage = input.value;
        this.chatMessageCharCount = this.currentMessage.length;
    }

    private getChatMessage(): null|BotMessageObject|AgentMessageObject
    {
        let input = this.messageContentComponent.$refs.chatMessageInput;
        if(!(input instanceof HTMLTextAreaElement)) {
            return null;
        }

        let message = this.sanitizeHtml(input.value.trim());
        if(message === "") {
            // input is empty signal
            if (!window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
                input.animate([
                    {transform: 'translateX(0)'},
                    {transform: 'translateX(6px)'},
                    {transform: 'translateX(0px)'},
                    {transform: 'translateX(6px)'},
                    {transform: 'translateX(0)'}
                ], {
                    duration: 400,
                    easing: 'ease-in-out'
                });
            }

            return null;
        }

        if(this.widget.activeChat === null) {
            return null;
        }

        const speechToText = this.isInputSpeechToText(input);
        return this.widget.getMessageObject(message, speechToText);
    }

    sendMessage():void {
        if(this.systemErrorMessage !== null) {
            return;
        }

        let messageObject = this.getChatMessage();
        if (messageObject === null) {
            return;
        }

        if (!this.hasSentFirstMessage) {
            this.placeholder = 'Weitere Frage stellen';
            this.hasSentFirstMessage = true;
        }

        this.showBotSuggestions = false;
        this.switchToAgentChatButtonVisibility = false;
        if(this.widget.activeChat instanceof AgentChat) {
            this.widget.setEndClientTyping();
            this.initClientTyping = true;
        }
        else {
            this.addSystemMessage(SystemMessage.LOAD_BOT_ANSWER);
        }

        let addMessageToContent = (message:string, id: string, chatType: ChatType, type: string) => this.addMessageToContent(message, id, chatType, type)
        this.widget.sendChatMessage(addMessageToContent, messageObject);
        this.currentMessage = '';
        this.updateCharCount();

        const input = this.messageContentComponent.$refs.chatMessageInput;
        if (input instanceof HTMLTextAreaElement) {
            this.setInputSpeechToTextAttribute(input, false);
        }

        if(this.widget.activeChat instanceof BotChat) {
            this.messageContentComponent.$dispatch('feedback-reset')
            this.disableMessageInput();
        }
    }

    sendSuggestion() {
        // this.$el ist im CSP-Build das geklickte Element
        const elementNode = (this as unknown as AlpineMagics<MessageContent>).$el;
        const suggestionText = elementNode.getAttribute('data-suggestion') ?? '';
        const input = this.messageContentComponent.$refs.chatMessageInput as HTMLTextAreaElement;
        if (input) {
            input.value = suggestionText;
            this.setInputSpeechToTextAttribute(input, false);
            this.sendMessage();
        }
    }

    async addMessageToContent(
        text: string,
        id: string,
        chatType: ChatType,
        type: string,
        author: string = '',
        time: string = '',
        messageId: string = '',
        responseId: string = '',
        htmlSafe: boolean = false,
    ) {
        if (!htmlSafe) {
            text = this.sanitizeHtml(text);
        }

        let outputMessage = MessageFactory.createOutputMessage(
            text,
            id,
            chatType,
            type,
            author,
            time,
            messageId,
            responseId
        );

        if (outputMessage.type === 'answer') {
            this.removeSystemMessage(SystemMessage.LOAD_BOT_ANSWER);
            if (this.componentConfig.allowAgentChat) {
                this.switchToAgentChatButtonVisibility = true;
            }
        }

        this.messages.push(outputMessage);

        await this.initializeFeedbackComponent(chatType, outputMessage);
    }

    private async initializeFeedbackComponent(chatType: ChatType, outputMessage: OutputMessageObject) {
        if (chatType !== ChatType.BOT_CHAT) {
            return;
        }

        if (outputMessage.type !== 'answer') {
            return;
        }

        const messageContent = this as unknown as AlpineMagics<MessageContent>;
        await messageContent.$nextTick();
        const containerId = 'chat-widget-feedback-inner-content_' + outputMessage.id;

        const feedbackContainer = messageContent.$el.querySelector('#' + containerId) as HTMLDivElement;
        if (feedbackContainer) {
            feedbackContainer.innerHTML = feedbackButtons;
            Alpine.initTree(feedbackContainer);
        }
    }

    getMessageText(message: OutputMessageObject):string {
        return this.widget.getOutputMessageText(message);
    }

    getBotHistoryMessageText(message: OutputMessageObject):string {
        return this.widget.getOutputMessageText(message);
    }

    isSalutationSie(): boolean {
        return this.widgetConfig.salutation === Salutation.SIE;
    }

    protected getMessageTime(message: OutputMessageObject|undefined):string {
        if (message?.type === 'question') {
            return '';
        }

        let time = message?.time ?? '';

        if (time !== '') {
            time = this.getTimeString(time);
        }
        return time;
    }

    protected getTimeString(time: string|Date): string
    {
        if (typeof time === 'string' && time.trim() === '') {
            return '';
        }

        const date = new Date(time);

        return new Intl.DateTimeFormat("de-DE", {
            hour: "2-digit",
            minute: "2-digit",
            hourCycle: "h23",
            timeZone: "Europe/Berlin"
        }).format(date);
    }

    getSendingStatus(message: OutputMessageObject):string {
        if (message.type !== 'question') {
            return '';
        }
        let id = message.id;
        if (typeof id !== 'string') {
            return '';
        }

        if (message.status === MessageSendState.NONE) {
            return '';
        }
        if (message.status === MessageSendState.SEND) {
            return '&check;';
        }
        if (message.status === MessageSendState.RECEIVED) {
            return '&check;&check;';
        }

        return '';
    }

    getSendingStatusTooltip(message: OutputMessageObject):string {
        if (message.type !== 'question') {
            return '';
        }
        let id = message.id;
        if (typeof id !== 'string') {
            return '';
        }

        if (message.status === MessageSendState.NONE) {
            return '';
        }
        if (message.status === MessageSendState.SEND) {
            return 'gesendet';
        }
        if (message.status === MessageSendState.RECEIVED) {
            return 'empfangen';
        }

        return '';
    }

    sanitizeHtml(html: string) {
        return DOMPurify.sanitize(html,
            {
                ALLOWED_TAGS: ['a', 'p', 'br', 'strong', 'em', 'li', 'ol', 'ul', 'span', 'div'],
                ALLOWED_ATTR: ['href','target','rel','class'],
                ALLOW_DATA_ATTR: true,
                ALLOW_UNKNOWN_PROTOCOLS: false
            });
    }

    switchToAgentChat():void {
        this.addAgentConnectSystemMessage();
        this.messageContentComponent.$dispatch('feedback-reset');
        this.messageContentComponent.$dispatch('switch-to-agent-chat');
        this.widget.switchToAgentChat(this.messages, this.botHistoryMessages);
        if(!this.widget.hasAiHistoryMessages()) {
            this.showBotGreeting = false;
            this.showBotSuggestions = false;
        }
    }

    addAgentConnectSystemMessage(): void {
        let messageId = SystemMessage.CONNECT_TO_AGENT;
        this.addSystemMessage(messageId);
        this.agentConnectMessageAdded = true;
    }

    disableMessageInput(): void {
        const input = this.messageContentComponent.$refs.chatMessageInput;
        const sendButton = this.messageContentComponent.$refs.sendButton;
        const currentMessage = input.closest('#chat-widget-current-message');

        if(currentMessage instanceof HTMLDivElement) {
            currentMessage.classList.add('disabled');
        }

        if(input instanceof HTMLInputElement) {
            input.setAttribute('disabled', 'disabled');
        }

        if(sendButton instanceof HTMLButtonElement) {
            sendButton.setAttribute('disabled', 'disabled');
        }
    }

    enableMessageInput(): void {
        if(this.systemErrorMessage !== null) {
            return;
        }

        const input = this.messageContentComponent.$refs.chatMessageInput;
        const sendButton = this.messageContentComponent.$refs.sendButton;
        const currentMessage = input.closest('#chat-widget-current-message');

        if(currentMessage instanceof HTMLDivElement && currentMessage.classList.contains('disabled')) {
            currentMessage.classList.remove('disabled');
        }

        if(input instanceof HTMLInputElement && input.hasAttribute('disabled')) {
            input.removeAttribute('disabled');
        }

        if(sendButton instanceof HTMLButtonElement && sendButton.hasAttribute('disabled')) {
            sendButton.removeAttribute('disabled');
        }
    }

    updateOnlineAgentName(name: string): void {
        this.onlineAgentName = name;
    }

    closeChat( isError: boolean|null = null):void {
        if( typeof isError !== 'boolean') {
            isError = this.isError;
        }
        this.widget.closeChatByUser(isError ?? this.isError);
    }

    closeConnection(): void {
        this.widget.activeChat?.closeWebsocketConnection();
    }
}

const messageContentFactory: MessageContentFactory = (
    widget: ChatWidget, widgetConfig:
    WidgetConfig,
    componentConfig: ComponentConfig) => {
    return new MessageContent(widget, widgetConfig, componentConfig);
}

export default messageContentFactory;